import React, { useMemo } from "react";
import { AlertTriangle, Bed, CalendarCheck, Plus, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { ROOM_PAX_DEFAULT, ROOM_TYPES, aggregateAccommodation, fmtEUR, nightsBetween, uid } from "./utils";
import { HotelAutocomplete } from "./HotelAutocomplete";
import { RoomConfigEditor } from "./RoomConfigEditor";
import HotelPerksEditor from "./HotelPerksEditor";

export function AccommodationsBlock({ itn, schedSave, markup, onOrient }) {
  // Build rooms from the itinerary default config.
  const buildDefaultRooms = () => {
    const out = [];
    const cfg = itn.room_config || [];
    (cfg.length > 0 ? cfg : [{ room_type: "doble", pax: 2, quantity: 1 }]).forEach((rc) => {
      for (let i = 0; i < (rc.quantity || 1); i++) {
        out.push({
          room_id: uid("room"),
          room_type: rc.room_type, pax: rc.pax,
          price_per_night_excl: 0, price_per_night_incl: 0, currency: "EUR",
        });
      }
    });
    return out;
  };

  const add = () => {
    // Leave the dates EMPTY on new rows so the agent explicitly picks the
    // stay range. Pre-filling with the trip's start/end caused false
    // "solapamiento" warnings whenever an agent added the same hotel twice
    // (typical use case: client stays at Hotel X on day 1-3 AND again on
    // day 10-12). Now every new row starts blank and the overlap detector
    // ignores it until the user provides both dates.
    const newAcc = {
      acc_id: uid("acc"),
      date_from: "", date_to: "",
      name: "", price_tax_excl: 0, price_tax_incl: 0, price: 0, currency: "EUR",
      rooms: buildDefaultRooms(),
    };
    schedSave({ ...itn, accommodations: [...(itn.accommodations || []), newAcc] });
  };

  const upd = (idx, patch) => {
    const synced = { ...patch };
    if ("price_tax_incl" in synced) synced.price = synced.price_tax_incl;
    const next = [...(itn.accommodations || [])];
    next[idx] = { ...next[idx], ...synced };
    schedSave({ ...itn, accommodations: next });
  };

  // Room CRUD on an accommodation. After any change re-derive the
  // accommodation aggregate (mode-aware, see aggregateAccommodation).
  // The day timeline does NOT need to be touched — the read-only stay chips
  // rendered by DayBlock pull live data from `accommodations[]` on every
  // render.
  const updateRooms = (idx, newRooms) => {
    const accs = [...(itn.accommodations || [])];
    accs[idx] = aggregateAccommodation({ ...accs[idx], rooms: newRooms });
    schedSave({ ...itn, accommodations: accs });
  };

  // Switch how prices are typed for this hotel. Normalise in the CURRENT
  // mode first so both pairs are consistent, then re-aggregate in the new
  // one — the numbers the agent sees don't jump.
  const setPriceMode = (idx, mode) => {
    const accs = [...(itn.accommodations || [])];
    const normalised = aggregateAccommodation(accs[idx]);
    accs[idx] = aggregateAccommodation({ ...normalised, room_price_mode: mode });
    schedSave({ ...itn, accommodations: accs });
  };

  const addRoom = (idx) => {
    const acc = (itn.accommodations || [])[idx];
    const rooms = [...(acc.rooms || []), {
      room_id: uid("room"), room_type: "doble", pax: 2,
      price_per_night_excl: 0, price_per_night_incl: 0, price_total_excl: 0, price_total_incl: 0, currency: "EUR",
    }];
    updateRooms(idx, rooms);
  };

  const removeRoom = (idx, room_id) => {
    const acc = (itn.accommodations || [])[idx];
    updateRooms(idx, (acc.rooms || []).filter((r) => r.room_id !== room_id));
  };

  const patchRoom = (idx, room_id, patch) => {
    const acc = (itn.accommodations || [])[idx];
    const rooms = (acc.rooms || []).map((r) => {
      if (r.room_id !== room_id) return r;
      const merged = { ...r, ...patch };
      if ("room_type" in patch && !("pax" in patch)) {
        merged.pax = ROOM_PAX_DEFAULT[patch.room_type] || merged.pax;
      }
      return merged;
    });
    updateRooms(idx, rooms);
  };

  const fetchOrient = (idx, a) => {
    onOrient?.({
      hotelName: a.name,
      city: null,
      checkin: a.date_from || itn.start_date,
      checkout: a.date_to || itn.end_date,
      adults: itn.num_travelers || 2,
      onApply: (pricePerNight) => {
        const dFrom = a.date_from ? new Date(a.date_from) : null;
        const dTo = a.date_to ? new Date(a.date_to) : null;
        const n = (dFrom && dTo) ? Math.max(1, Math.round((dTo - dFrom) / 86400000)) : 1;
        const total = pricePerNight * n;
        upd(idx, { price_tax_incl: total, price_tax_excl: total / 1.10 });
        toast.success(`Aplicado · ${pricePerNight}€/noche × ${n} noches = ${Math.round(total)}€`);
      },
    });
  };

  // Spread Check-in/Mid/Check-out service rows across the days.
  // LEGACY ONLY — kept so `delAndUnspread` can scrub carrier services that
  // were created by the old auto-spread implementation. New writes never
  // touch `days[].services[]` because the stay chips are now derived from
  // `accommodations[]` at render time.
  const unspreadAccommodation = (acc_id) => (itn.days || []).map((d) => ({
    ...d,
    services: (d.services || []).filter((s) => s.acc_id !== acc_id),
  }));

  // Generic patch on a single accommodation. No longer mutates the day
  // matrix — the read-only chips in DayBlock are re-derived from
  // `accommodations[]` on every render.
  const updWithSpread = (idx, patch, hotelRecord = null) => {
    const synced = { ...patch };
    if ("price_tax_incl" in synced) synced.price = synced.price_tax_incl;
    const list = [...(itn.accommodations || [])];
    const newAcc = { ...list[idx], ...synced };
    list[idx] = newAcc;
    // Re-derive aggregate from rooms when we have all the inputs (date_from,
    // date_to, rooms). Hotel-record fallback covers the "user just picked a
    // hotel from the catalog" path.
    if (newAcc.name && newAcc.date_from && newAcc.date_to) {
      const nights = Math.max(1, Math.round((new Date(newAcc.date_to) - new Date(newAcc.date_from)) / 86400000));
      const rooms = newAcc.rooms || [];
      const numRooms = Math.max(1, rooms.length || 1);
      const roomsPriced = rooms.some((r) => (r.price_per_night_incl || r.price_per_night_excl || r.price_total_incl || r.price_total_excl || 0) > 0);
      if (rooms.length > 0 && roomsPriced) {
        // Mode-aware: in "total" mode a date change keeps the stay totals
        // the hotel quoted and only re-derives €/noche.
        list[idx] = aggregateAccommodation(newAcc);
        schedSave({ ...itn, accommodations: list });
        return;
      }
      let avgIncl, avgExcl;
      if (hotelRecord?.price_per_night_incl) {
        avgIncl = hotelRecord.price_per_night_incl;
        avgExcl = hotelRecord.price_per_night_excl || hotelRecord.price_per_night_incl;
      } else {
        avgIncl = (newAcc.price_tax_incl || 0) / Math.max(1, nights * numRooms);
        avgExcl = (newAcc.price_tax_excl || newAcc.price_tax_incl || 0) / Math.max(1, nights * numRooms);
      }
      avgIncl = Math.round(avgIncl * 100) / 100;
      avgExcl = Math.round(avgExcl * 100) / 100;
      const totalQty = nights * numRooms;
      list[idx] = {
        ...newAcc,
        price_tax_incl: Math.round(avgIncl * totalQty * 100) / 100,
        price_tax_excl: Math.round(avgExcl * totalQty * 100) / 100,
        price: Math.round(avgIncl * totalQty * 100) / 100,
      };
    }
    schedSave({ ...itn, accommodations: list });
  };

  const pickHotel = (idx, hotel) => {
    const accs = [...(itn.accommodations || [])];
    const current = accs[idx] || {};
    const existing = (current.rooms && current.rooms.length > 0) ? current.rooms : buildDefaultRooms();
    const nights = nightsBetween(current.date_from, current.date_to) || 1;
    // Catalog prices are €/noche; seed both pairs so the aggregate is right
    // whichever mode this hotel row is in.
    const rooms = existing.map((r) => {
      const nExcl = hotel.price_per_night_excl || hotel.price_per_night_incl || 0;
      const nIncl = hotel.price_per_night_incl || hotel.price_per_night_excl || 0;
      return { ...r, price_per_night_excl: nExcl, price_per_night_incl: nIncl,
               price_total_excl: Math.round(nExcl * nights * 100) / 100, price_total_incl: Math.round(nIncl * nights * 100) / 100 };
    });
    accs[idx] = aggregateAccommodation({ ...current, name: hotel.name, rooms });
    // No day-matrix mutation — DayBlock's stay chips re-derive themselves
    // from `accommodations[]` on the next render.
    schedSave({ ...itn, accommodations: accs });
  };

  const delAndUnspread = (idx) => {
    const accId = (itn.accommodations || [])[idx]?.acc_id;
    const newDays = unspreadAccommodation(accId);
    schedSave({
      ...itn,
      accommodations: (itn.accommodations || []).filter((_, i) => i !== idx),
      days: newDays,
    });
  };

  const overlaps = useMemo(() => {
    const accs = (itn.accommodations || []).filter((a) => a.name && a.date_from && a.date_to);
    const out = [];
    for (let i = 0; i < accs.length; i++) {
      for (let j = i + 1; j < accs.length; j++) {
        const a = accs[i], b = accs[j];
        const aFrom = new Date(a.date_from), aTo = new Date(a.date_to);
        const bFrom = new Date(b.date_from), bTo = new Date(b.date_to);
        if (isNaN(aFrom) || isNaN(aTo) || isNaN(bFrom) || isNaN(bTo)) continue;
        if (aFrom < bTo && bFrom < aTo) {
          const overlapStart = aFrom > bFrom ? aFrom : bFrom;
          const overlapEnd = aTo < bTo ? aTo : bTo;
          const days = Math.max(0, Math.round((overlapEnd - overlapStart) / 86400000));
          if (days > 0) out.push({ a, b, days });
        }
      }
    }
    return out;
  }, [itn.accommodations]);

  return (
    <div className="mt-10">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 smallcaps"><Bed size={13}/> Alojamientos (sumario)</div>
        <button onClick={add} className="text-xs inline-flex items-center gap-1 px-2 py-1 hover:bg-clay-200" data-testid="add-accommodation">
          <Plus size={12}/> Añadir alojamiento
        </button>
      </div>

      {overlaps.length > 0 && (
        <div
          data-testid="acc-overlap-warning"
          className="mb-3 border border-destructive bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          <div className="font-semibold mb-1 flex items-center gap-2">
            <AlertTriangle size={14}/>
            {overlaps.length === 1 ? "Solapamiento detectado en alojamientos" : `${overlaps.length} solapamientos detectados en alojamientos`}
          </div>
          <ul className="list-disc pl-5 space-y-0.5 text-[12px]">
            {overlaps.map((o) => (
              <li key={`${o.a.acc_id}-${o.b.acc_id}`}>
                <span className="font-semibold">{o.a.name}</span> ({o.a.date_from} → {o.a.date_to})
                {" "}solapa con{" "}
                <span className="font-semibold">{o.b.name}</span> ({o.b.date_from} → {o.b.date_to})
                {" "}— {o.days} {o.days === 1 ? "día" : "días"} en conflicto.
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="border border-clay-300 bg-white">
        {(itn.accommodations || []).length === 0 ? (
          <div className="p-4 text-sm text-clay-700">Opcional. Añade alojamientos resumidos por estancia.</div>
        ) : (
          <>
            <div className="grid grid-cols-[1fr_120px_120px_120px_90px_28px_28px] gap-2 px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-clay-700 font-semibold bg-clay-50 border-b border-clay-300">
              <div>Hotel / Apartamento</div><div>Desde</div><div>Hasta</div>
              <div className="text-right">Con IVA</div><div className="text-right">PVP</div><div></div><div></div>
            </div>
            {(itn.accommodations || []).map((a, idx) => {
              const incl = a.price_tax_incl || a.price || 0;
              const pvp = incl * (1 + (markup || 0) / 100);
              const rooms = a.rooms || [];
              const nights = nightsBetween(a.date_from, a.date_to);
              const roomsTotalPax = rooms.reduce((s, r) => s + (r.pax || 0), 0);
              const usingRooms = rooms.length > 0;
              const totalMode = a.room_price_mode === "total";
              const nightsOr1 = nights || 1;
              const fmtHint = (n) => (Math.round(n * 100) / 100).toLocaleString("es-ES", { maximumFractionDigits: 2 });
              return (
                <div key={a.acc_id}
                     id={`acc-row-${a.acc_id}`}
                     data-testid={`acc-row-${a.acc_id}`}
                     className="border-t border-clay-300 transition-shadow">
                  <div className="grid grid-cols-[1fr_120px_120px_120px_90px_28px_28px] gap-2 px-3 py-2 items-center text-sm">
                    <HotelAutocomplete
                      value={a.name}
                      onTextChange={(v) => updWithSpread(idx, { name: v })}
                      onPick={(h) => pickHotel(idx, h)}
                      placeholder="Buscar hotel del catálogo…"
                    />
                    <input type="date" className="bg-transparent outline-none tabular" value={a.date_from || ""} onChange={(e) => updWithSpread(idx, { date_from: e.target.value })} />
                    <input type="date" className="bg-transparent outline-none tabular" value={a.date_to || ""} onChange={(e) => updWithSpread(idx, { date_to: e.target.value })} />
                    <div className="flex items-center gap-1 justify-end">
                      <span className="text-clay-500 text-[10px]">€</span>
                      <input
                        type="number" min="0" step="0.01"
                        className={`bg-transparent text-right outline-none tabular w-full ${usingRooms ? "text-clay-500" : ""}`}
                        value={incl}
                        readOnly={usingRooms}
                        title={usingRooms ? "Calculado a partir de las habitaciones" : "Editable"}
                        onChange={(e) => upd(idx, { price_tax_incl: parseFloat(e.target.value || "0") })}
                      />
                    </div>
                    <div className="text-right tabular font-semibold">{fmtEUR(pvp)}</div>
                    <button
                      data-testid={`orient-${idx}`}
                      onClick={() => fetchOrient(idx, a)}
                      title="Buscar precio orientativo · histórico + Expedia"
                      className="text-clay-700 hover:text-terracotta hover:bg-clay-100 p-1 border border-clay-300 flex items-center justify-center"
                    ><Search size={14}/></button>
                    <button onClick={() => delAndUnspread(idx)} className="text-clay-500 hover:text-destructive p-1"><Trash2 size={14}/></button>
                  </div>
                  <div className="pl-4 pr-3 pb-3 -mt-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-clay-700 mb-1 flex items-center gap-2 flex-wrap">
                      Habitaciones
                      <span className="font-semibold text-clay-900" data-testid={`rooms-summary-${idx}`}>
                        {rooms.length} · {roomsTotalPax} pax · {nights || 0} noches
                      </span>
                      {itn.num_travelers && roomsTotalPax !== itn.num_travelers && (
                        <span className="text-amber-700 normal-case tracking-normal text-[11px]">
                          (viaje de {itn.num_travelers} pax)
                        </span>
                      )}
                      {/* How room prices are typed for this hotel: €/noche
                          (legacy) or the stay total per room as quoted by
                          the hotel — avoids hand-dividing by nights. */}
                      <span className="inline-flex border border-clay-300 text-[10px] tracking-[0.12em] uppercase" data-testid={`price-mode-${idx}`}
                            title="Cómo se introduce el precio de cada habitación">
                        <button type="button" onClick={() => !totalMode || setPriceMode(idx, "per_night")}
                                data-testid={`price-mode-night-${idx}`}
                                className={`px-2 py-0.5 ${!totalMode ? "bg-clay-900 text-white" : "bg-white text-clay-700 hover:bg-clay-100"}`}>€/noche</button>
                        <button type="button" onClick={() => totalMode || setPriceMode(idx, "total")}
                                data-testid={`price-mode-total-${idx}`}
                                className={`px-2 py-0.5 ${totalMode ? "bg-clay-900 text-white" : "bg-white text-clay-700 hover:bg-clay-100"}`}>€ total estancia</button>
                      </span>
                      {/* "Aplicar estancia" button — confirms the stay
                          will surface as read-only chips in every day
                          between date_from and date_to. Clicking scrolls
                          to the first matching day so the agent sees the
                          chip and feels reassured nothing duplicated. */}
                      {(() => {
                        const matchingDays = (itn.days || []).filter((d) => {
                          if (!d.date || !a.date_from || !a.date_to) return false;
                          const dd = new Date(d.date);
                          return !isNaN(dd) && dd >= new Date(a.date_from) && dd <= new Date(a.date_to);
                        });
                        const canApply = matchingDays.length > 0 && a.name;
                        const dayNumbers = matchingDays
                          .map((d) => d.day_number)
                          .filter((n) => n != null);
                        const label = canApply
                          ? `Aplicada${dayNumbers.length > 0
                              ? ` · días ${dayNumbers[0]}${dayNumbers.length > 1 ? `–${dayNumbers[dayNumbers.length - 1]}` : ""}`
                              : ""}`
                          : "Aplicar estancia";
                        return (
                          <button
                            type="button"
                            disabled={!canApply}
                            data-testid={`apply-stay-${a.acc_id}`}
                            onClick={() => {
                              if (!canApply) return;
                              const firstDay = matchingDays[0];
                              // Find any element in DayBlock that anchors
                              // the chip for this acc_id (data-testid is
                              // stable across renders).
                              const chip = document.querySelector(
                                `[data-testid="stay-chip-${a.acc_id}"]`
                              );
                              const dayEl = chip
                                ? chip.closest("[data-day-id]")
                                : document.querySelector(`[data-day-id="${firstDay.day_id}"]`);
                              if (dayEl) {
                                dayEl.scrollIntoView({ behavior: "smooth", block: "center" });
                              } else if (chip) {
                                chip.scrollIntoView({ behavior: "smooth", block: "center" });
                              }
                              if (chip) {
                                chip.classList.add("ring-2", "ring-terracotta");
                                setTimeout(() => chip.classList.remove("ring-2", "ring-terracotta"), 1800);
                              }
                              toast.success(
                                `Estancia aplicada en ${matchingDays.length} día${matchingDays.length === 1 ? "" : "s"}`
                              );
                            }}
                            className={`ml-auto inline-flex items-center gap-1.5 px-2 py-1 border text-[10px] uppercase tracking-[0.2em]
                              ${canApply
                                ? "border-pine-soft bg-pine-soft/30 text-pine hover:bg-pine-soft/60 cursor-pointer"
                                : "border-clay-200 bg-clay-50 text-clay-400 cursor-not-allowed"}`}
                            title={canApply
                              ? "Aplica esta estancia en los días que cubre (visible en la línea de tiempo)"
                              : "Necesita nombre + fechas de entrada y salida"}
                          >
                            <CalendarCheck size={12} /> {label}
                          </button>
                        );
                      })()}
                    </div>
                    {rooms.length === 0 ? (
                      <div className="text-xs text-clay-500 italic mb-1">Sin habitaciones — usando precio plano</div>
                    ) : (
                      <div className="space-y-1">
                        {rooms.map((r) => (
                          <div
                            key={r.room_id}
                            className="grid grid-cols-[1fr_120px_120px_120px_90px_28px_28px] gap-2 items-center text-xs border-l-2 border-clay-200 pl-2"
                            data-testid={`room-${r.room_id}`}
                          >
                            <div className="flex items-center gap-2 text-[11px]">
                              <select
                                className="bg-white border border-clay-200 px-1 py-0.5 text-xs w-24"
                                value={r.room_type}
                                onChange={(e) => patchRoom(idx, r.room_id, { room_type: e.target.value })}
                                data-testid={`room-type-${r.room_id}`}
                              >
                                {ROOM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                              </select>
                              <span className="text-clay-500">·</span>
                              <input
                                type="number" min="1" max="20"
                                className="bg-white border border-clay-200 px-1 py-0.5 text-right tabular w-12"
                                value={r.pax || 1}
                                onChange={(e) => patchRoom(idx, r.room_id, { pax: parseInt(e.target.value || "1", 10) })}
                                title="Pax en esta habitación"
                                data-testid={`room-pax-${r.room_id}`}
                              />
                              <span className="text-clay-500 text-[11px]">pax · {totalMode ? "€ total estancia" : "€/noche"}</span>
                            </div>
                            <div />
                            <div />
                            <div className="flex items-center gap-1 justify-end">
                              <span className="text-clay-500 text-[10px]">€</span>
                              <input
                                type="number" min="0" step="0.01"
                                className={`bg-white border px-1 py-0.5 text-right tabular w-full font-semibold ${totalMode ? "border-terracotta/70 ring-2 ring-terracotta/10" : "border-clay-200"}`}
                                value={(totalMode ? r.price_total_incl : r.price_per_night_incl) || 0}
                                onChange={(e) => {
                                  // The editor only exposes the VAT-incl price; keep excl in
                                  // step so the "sin IVA" subtotal never goes stale.
                                  const v = parseFloat(e.target.value || "0");
                                  patchRoom(idx, r.room_id, totalMode
                                    ? { price_total_incl: v, price_total_excl: v }
                                    : { price_per_night_incl: v, price_per_night_excl: v });
                                }}
                                title={totalMode ? "Precio total de la estancia para esta habitación, con IVA (tal cual lo pasa el hotel)" : "Precio por noche con IVA"}
                                data-testid={`room-incl-${r.room_id}`}
                              />
                            </div>
                            <div className="col-span-2 text-[11px] text-clay-500 tabular whitespace-nowrap" data-testid={`room-hint-${r.room_id}`}>
                              {totalMode
                                ? `= ${fmtHint((r.price_total_incl || 0) / nightsOr1)} €/noche`
                                : `= ${fmtHint((r.price_per_night_incl || 0) * nightsOr1)} € estancia`}
                            </div>
                            <button
                              onClick={() => removeRoom(idx, r.room_id)}
                              className="text-clay-400 hover:text-destructive p-1 flex items-center justify-center"
                              title="Quitar habitación"
                              data-testid={`room-del-${r.room_id}`}
                            ><X size={12}/></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => addRoom(idx)}
                      className="mt-1 text-[11px] inline-flex items-center gap-1 px-1.5 py-0.5 text-clay-700 hover:text-terracotta hover:bg-clay-100"
                      data-testid={`add-room-${idx}`}
                    >
                      <Plus size={10}/> Añadir habitación
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      <HotelPerksEditor
        perks={itn.hotel_perks}
        onChange={(hotel_perks) => schedSave({ ...itn, hotel_perks })}
        testid="itn-hotel-perks"
      />
    </div>
  );
}

// Re-export the room config editor from the same module barrel.
export { RoomConfigEditor };
